import { Users, BarChart3, MessageSquare, ShieldCheck, Building2, Activity } from "lucide-react";
import { useTranslation } from "react-i18next";

const CONTENT = {
  es: {
    eyebrow: "Nuevo · Plan Corporativo",
    titleTop: "Dale a tu equipo un",
    titleAccent: "espacio con reglas claras",
    description: "El plan Corporativo trae un panel de admin dedicado, estadísticas de uso por persona, chat interno de organización y facturación centralizada — todo con la privacidad de que",
    descriptionStrong: "el admin ve la actividad, nunca el contenido",
    badgeMin: "Mínimo 3 usuarios",
    badgeStorage: "50 MB por miembro",
    badgeChat: "Chat de equipo",
    highlights: [
      { title: "Panel de administración de organización", desc: "Un admin dedicado gestiona a los miembros, ve los tableros que existen y controla la suscripción del equipo." },
      { title: "Estadísticas de accesos y uso", desc: "El admin ve la frecuencia con la que cada miembro accede a la plataforma y cuántos tableros usa — sin espiar el contenido." },
      { title: "Chat interno de la organización", desc: "Mensajes directos entre miembros y broadcasts del admin a todo el equipo, todo dentro de Sprintroll." },
      { title: "Escala por asiento (mín. 3)", desc: "Suma miembros cuando quieras. Cada asiento adicional se cobra al mismo precio, sin cargos ocultos." },
      { title: "Privacidad para tu equipo", desc: "El admin ve la actividad, no el contenido de los tableros. Tus datos no se usan para entrenar IA." },
      { title: "50 MB por usuario", desc: "Cada miembro tiene su propio espacio para adjuntar archivos a las tarjetas." },
    ],
  },
  en: {
    eyebrow: "New · Corporate Plan",
    titleTop: "Give your team a",
    titleAccent: "space with clear rules",
    description: "Corporate ships with a dedicated admin panel, per-person usage stats, an in-org chat and centralized billing — with the peace of mind that",
    descriptionStrong: "the admin sees activity, never the content",
    badgeMin: "Min 3 users",
    badgeStorage: "50 MB per member",
    badgeChat: "Team chat",
    highlights: [
      { title: "Org admin panel", desc: "A dedicated admin manages members, sees how many boards exist and controls the team subscription." },
      { title: "Access & usage stats", desc: "The admin sees how often each member logs in and how many boards they use — without peeking into the content." },
      { title: "In-org team chat", desc: "Direct messages between members and admin broadcasts, all inside Sprintroll." },
      { title: "Scale per seat (min 3)", desc: "Add members whenever you need. Every extra seat costs the same — no hidden fees." },
      { title: "Privacy for your team", desc: "The admin sees activity, not board content. Your data is never used to train AI." },
      { title: "50 MB per user", desc: "Every member has their own space to attach files to cards." },
    ],
  },
};

const ICONS = [Building2, BarChart3, MessageSquare, Users, ShieldCheck, Activity];
const COLORS = ["blue", "violet", "emerald", "amber", "rose", "sky"];

const ACCENT = {
  blue:    "bg-blue-500/15 text-blue-600 dark:text-blue-300",
  violet:  "bg-violet-500/15 text-violet-600 dark:text-violet-300",
  emerald: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-300",
  amber:   "bg-amber-500/15 text-amber-600 dark:text-amber-300",
  rose:    "bg-rose-500/15 text-rose-600 dark:text-rose-300",
  sky:     "bg-sky-500/15 text-sky-600 dark:text-sky-300",
};

export default function TeamAdminSection() {
  const { i18n } = useTranslation();
  const lang = (i18n.language || "es").startsWith("en") ? "en" : "es";
  const t = CONTENT[lang];

  return (
    <section
      className="relative py-20 md:py-28 border-t border-white/40 dark:border-white/5"
      data-testid="landing-team-admin"
    >
      <div className="max-w-6xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-10 items-end">
          <div>
            <span
              className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-semibold text-blue-600 dark:text-blue-400"
              data-testid="landing-team-admin-eyebrow"
            >
              <Sparkle /> {t.eyebrow}
            </span>
            <h2 className="font-display text-3xl md:text-5xl font-semibold tracking-tight mt-3 leading-[1.05]">
              {t.titleTop}<br />
              <span className="text-blue-600 dark:text-blue-300">{t.titleAccent}</span>.
            </h2>
            <p className="text-muted-foreground mt-5 text-base md:text-lg max-w-lg">
              {t.description} <strong>{t.descriptionStrong}</strong>.
            </p>
            <div className="mt-6 flex flex-wrap gap-2 text-xs">
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 px-3 py-1 font-medium">
                {t.badgeMin}
              </span>
              <span className="inline-flex items-center gap-1 rounded-full bg-blue-500/15 text-blue-700 dark:text-blue-300 px-3 py-1 font-medium">
                {t.badgeStorage}
              </span>
              <span className="inline-flex items-center gap-1 rounded-full bg-violet-500/15 text-violet-700 dark:text-violet-300 px-3 py-1 font-medium">
                {t.badgeChat}
              </span>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            {t.highlights.map((h, i) => {
              const Icon = ICONS[i];
              const color = COLORS[i];
              return (
                <div
                  key={h.title}
                  data-testid={`team-admin-highlight-${i}`}
                  className="glass-card rounded-2xl p-5 hover:-translate-y-1 hover:shadow-xl hover:shadow-blue-500/10 transition"
                  style={{
                    animation: "fade-rise 0.55s ease-out both",
                    animationDelay: `${i * 60}ms`,
                  }}
                >
                  <div className={`h-10 w-10 rounded-xl ${ACCENT[color]} grid place-items-center`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <p className="font-display font-semibold text-base mt-4 leading-snug">{h.title}</p>
                  <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">{h.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

function Sparkle() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M12 2l2.09 5.26L20 9.27l-4.5 3.9L17.09 20 12 16.9 6.91 20l1.59-6.83L4 9.27l5.91-2.01L12 2z" />
    </svg>
  );
}
